(ns frontend.view.settings2.zap-vaults.view
  (:require [clojure.string :as string]
            [common.localization :as loc]
            [common.util :as cu]
            [frontend.page-components.default-page-layout :as default-page-layout]
            [frontend.page-components.zap-toolbar :as zap-toolbar]
            [frontend.routing :as routing]
            [frontend.state.current-session :as session]
            [frontend.ui.component.image-cropper.view :as image-cropper]
            [frontend.view.not-found :as not-found]
            [frontend.view.settings2.zap-vaults.event :as event]
            [frontend.view.settings2.zap-vaults.sub :as sub]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [ui.components.breadcrumbs.view :as breadcrumbs]
            [ui.components.button.view :as button]
            [ui.components.layout.footer :as footer]
            [ui.components.link.view :as link]
            [ui.components.text.view :as text]
            [ui.editors.labelled-input.view :as labelled-input]
            [ui.layout :as layout]))

(defn logo-config
  []
  (let [logo-url @(rf/subscribe [::sub/vault-logo])
        valid-image-url? (and (not (cu/blank-value? logo-url))
                              (string/starts-with? (str logo-url) "http"))]
    [:div
     {:class "flex flex-col gap-2 border border-solid border-base-light-border-paler p-4"}
     [text/h2
      (loc/tr :vault-config/vault-logo)]
     [:div
      {:class "flex flex-row gap-2 align-items-center"}
      (when valid-image-url?
        [:img
         {:class "max-w-[250px] max-h-[50px] w-auto h-auto object-contain\n"
          :src   logo-url}])
      [image-cropper/modal-image-cropper
       {:target-height    50
        :target-width     250
        :dropzone-content (when valid-image-url?
                            [:div
                             {:class (button/link-style nil)}
                             (loc/tr :replace-logo)])
        :round?           false
        :on-cropped       (fn [base64-image]
                            (rf/dispatch [::event/upload-logo base64-image]))
        :size             :xs}]]]))

(def state (r/atom {}))

(defn colors-config
  []
  (let [button-color @(rf/subscribe [::sub/button-color])
        text-color @(rf/subscribe [::sub/text-color])]
    [:div
     {:class "flex flex-col gap-2 border border-solid border-base-light-border-paler p-4"}
     [text/h2
      (loc/tr :zap-vault-colors)]
     [text/p
      (loc/tr :zap-vault-colors-info)]
     [labelled-input/view
      {:label       (loc/tr :vault-config/button-color)
       :value       button-color
       :editor-type :editor.type/hex-color
       :on-change   (fn [v]
                      (rf/dispatch [::event/set-button-color v]))}]
     [labelled-input/view
      {:label       (loc/tr :vault-config/text-color)
       :value       text-color
       :editor-type :editor.type/hex-color
       :on-change   (fn [v]
                      (rf/dispatch [::event/set-text-color v]))}]
     ]))

(defn back-url-config
  []
  (let [back-url @(rf/subscribe [::sub/back-url])
        back-url-editable? @(rf/subscribe [::sub/back-url-editable?])]
    [:div
     {:class "flex flex-col gap-2 border border-solid border-base-light-border-paler p-4"}
     [text/h2
      (loc/tr :zap-vault-back-url)]
     [text/p
      (loc/tr :zap-vault-back-url-info)]
     [labelled-input/view
      {:label     (loc/tr :vault-config/logout-url)
       :value     back-url
       :data-type :data.type/text-url
       :on-change (fn [v]
                    (rf/dispatch [::event/set-back-url v]))}]
     [:div
      {:class "flex flex-row gap-2"}
      [:div
       {:class "flex flex-row"}
       [labelled-input/label-elem
        {:label (loc/tr :vault-config/allow-url-modification?)}]]
      [:div
       {:class "flex-1 flex flex-row justify-content-end"}
       [labelled-input/view
        {:label       (if back-url-editable?
                        (loc/tr :editable?)
                        (loc/tr :not-editable?))
         :value       back-url-editable?
         :editor-type :editor.type/toggle
         :horizontal? true
         :on-change   (fn [v]
                        (rf/dispatch [::event/set-back-url-editable? v]))}]]]]))

(defn unauth-message-config
  []
  (let [message @(rf/subscribe [::sub/unauthorized-message])
        message-editable? @(rf/subscribe [::sub/unauthorized-message-editable?])]
    [:div
     {:class "flex flex-col gap-2 border border-solid border-base-light-border-paler p-4"}
     [text/h2
      (loc/tr :zap-vault-unauthorized-message)]
     [text/p
      (loc/tr :zap-vault-unauthorized-message-info)]
     [labelled-input/view
      {:label     (loc/tr :vault-config/no-access-message)
       :value     message
       :data-type :data.type/text-richtext
       :on-change (fn [v]
                    (rf/dispatch [::event/set-unauthorized-message v]))}]
     [:div
      {:class "flex flex-row gap-2"}
      [:div
       {:class "flex flex-row"}
       [labelled-input/label-elem
        {:label (loc/tr :vault-config/allow-unauthorized-message-modification?)}]]
      [:div
       {:class "flex-1 flex flex-row justify-content-end"}
       [labelled-input/view
        {:label       (if message-editable?
                        (loc/tr :editable?)
                        (loc/tr :not-editable?))
         :value       message-editable?
         :editor-type :editor.type/toggle
         :horizontal? true
         :on-change   (fn [v]
                        (rf/dispatch [::event/set-unauthorized-message-editable? v]))}]]]]))

(defn disclaimer-message-config
  []
  (let [message @(rf/subscribe [::sub/disclaimer-message])
        confirm-text @(rf/subscribe [::sub/disclaimer-confirm-text])
        message-editable? @(rf/subscribe [::sub/disclaimer-message-editable?])]
    [:div
     {:class "flex flex-col gap-2 border border-solid border-base-light-border-paler p-4"}
     [text/h2
      (loc/tr :zap-vault-disclaimer-message)]
     [text/p
      (loc/tr :zap-vault-disclaimer-message-info)]
     [labelled-input/view
      {:label     (loc/tr :vault-config/disclaimer-message)
       :value     message
       :data-type :data.type/text-richtext
       :on-change (fn [v]
                    (rf/dispatch [::event/set-disclaimer-message v]))}]
     [labelled-input/view
      {:label     (loc/tr :vault-config/disclaimer-confirm-text)
       :value     confirm-text
       :data-type :data.type/text-simple
       :on-change (fn [v]
                    (rf/dispatch [::event/set-disclaimer-confirm-text v]))}]
     [:div
      {:class "flex flex-row gap-2"}
      [:div
       {:class "flex flex-row"}
       [labelled-input/label-elem
        {:label (loc/tr :vault-config/allow-disclaimer-modification?)}]]
      [:div
       {:class "flex-1 flex flex-row justify-content-end"}
       [labelled-input/view
        {:label       (if message-editable?
                        (loc/tr :editable?)
                        (loc/tr :not-editable?))
         :value       message-editable?
         :editor-type :editor.type/toggle
         :horizontal? true
         :on-change   (fn [v]
                        (rf/dispatch [::event/set-disclaimer-message-editable? v]))}]]]]))

(defn members-permissions-config
  []
  (let [members-editable? @(rf/subscribe [::sub/members-editable?])]
    [:div
     {:class "flex flex-col gap-2 border border-solid border-base-light-border-paler p-4"}
     [:div
      {:class "flex flex-row gap-2"}
      [:div
       {:class "flex flex-row"}
       [labelled-input/label-elem
        {:label (loc/tr :vault-config/allow-members-configuration?)}]]
      [:div
       {:class "flex-1 flex flex-row justify-content-end"}
       [labelled-input/view
        {:label       (if members-editable?
                        (loc/tr :editable?)
                        (loc/tr :not-editable?))
         :value       members-editable?
         :editor-type :editor.type/toggle
         :horizontal? true
         :on-change   (fn [v]
                        (rf/dispatch [::event/set-members-editable? v]))}]]]]))

(defn teams-permissions-config
  []
  (let [teams-editable? @(rf/subscribe [::sub/teams-editable?])]
    [:div
     {:class "flex flex-col gap-2 border border-solid border-base-light-border-paler p-4"}
     [:div
      {:class "flex flex-row gap-2"}
      [:div
       {:class "flex flex-row"}
       [labelled-input/label-elem
        {:label (loc/tr :vault-config/allow-teams-configuration?)}]]
      [:div
       {:class "flex-1 flex flex-row justify-content-end"}
       [labelled-input/view
        {:label       (if teams-editable?
                        (loc/tr :editable?)
                        (loc/tr :not-editable?))
         :value       teams-editable?
         :editor-type :editor.type/toggle
         :horizontal? true
         :on-change   (fn [v]
                        (rf/dispatch [::event/set-teams-editable? v]))}]]]]))

(defn brand-config
  []
  (let [changes @(rf/subscribe [::sub/unsaved-changes])]
    [:div
     {:class "flex flex-col gap-1 min-h-0 h-full"}
     [:div
      {:class "flex flex-col gap-4 overflow-y-auto"}
      [logo-config]
      [colors-config]
      [back-url-config]
      [unauth-message-config]]
     [footer/footer
      [footer/left-align
       (when (seq changes)
         [button/secondary-hollow
          {:on-click #(rf/dispatch [::event/cancel])}
          (loc/tr :cancel)])]
      [footer/right-align
       [button/primary-solid
        {:on-click  (fn [_]
                      (rf/dispatch [::event/save changes]))
         :disabled? (empty? changes)}
        (loc/tr :save)]]]]))

(defn permissions-config
  []
  (let [changes @(rf/subscribe [::sub/unsaved-changes])]
    [:div
     {:class "flex flex-col gap-1 min-h-0 h-full"}
     [:div
      {:class "flex flex-col gap-4 overflow-y-auto"}
      [members-permissions-config]
      [teams-permissions-config]]
     [footer/footer
      [footer/left-align
       (when (seq changes)
         [button/secondary-hollow
          {:on-click #(rf/dispatch [::event/cancel])}
          (loc/tr :cancel)])]
      [footer/right-align
       [button/primary-solid
        {:on-click  (fn [_]
                      (rf/dispatch [::event/save changes]))
         :disabled? (empty? changes)}
        (loc/tr :save)]]]]))

(defn legal-config
  []
  (let [changes @(rf/subscribe [::sub/unsaved-changes])]
    [:div
     {:class "flex flex-col gap-1 min-h-0 h-full"}
     [:div
      {:class "flex flex-col gap-4 overflow-y-auto"}
      [disclaimer-message-config]]
     [footer/footer
      [footer/left-align
       (when (seq changes)
         [button/secondary-hollow
          {:on-click #(rf/dispatch [::event/cancel])}
          (loc/tr :cancel)])]
      [footer/right-align
       [button/primary-solid
        {:on-click  (fn [_]
                      (rf/dispatch [::event/save changes]))
         :disabled? (empty? changes)}
        (loc/tr :save)]]]]))

(defn sidebar
  []
  (let [active-tab @(rf/subscribe [::sub/active-tab])]
    [:div
     {:class "flex flex-row mb-2 @md:flex-col"}
     [link/side-nav-link {:active?  (= :brand active-tab)
                          :on-click (fn []
                                      (rf/dispatch [::event/set-active-tab :brand]))}
      (loc/tr :brand)]
     [link/side-nav-link {:active?  (= :legal active-tab)
                          :on-click (fn []
                                      (rf/dispatch [::event/set-active-tab :legal]))}
      (loc/tr :legal)]
     [link/side-nav-link {:active?  (= :permissions active-tab)
                          :on-click (fn []
                                      (rf/dispatch [::event/set-active-tab :permissions]))}
      (loc/tr :permissions)]]))

(defn content
  []
  (let [active-tab @(rf/subscribe [::sub/active-tab])]
    (case active-tab
      :legal
      [legal-config]
      :brand
      [brand-config]
      [permissions-config])))

(defn vaults-config
  []
  [layout/inline-container
   [layout/sidebar-main {:min-w "200px"
                         :max-w "300px"}
    [sidebar]
    [content]]])

(defn breadcrumbs
  []
  [breadcrumbs/container
   [breadcrumbs/item
    {:on-click (fn [_e]
                 (routing/set-view! [:account-settings :tab-key "config"]))}
    (loc/tr :settings/account-settings)]
   [breadcrumbs/item
    {:active? true}
    (loc/tr :settings/zap-vaults)]])

(defn page
  []
  [:<>
   [zap-toolbar/view nil breadcrumbs nil []]
   [:div
    {:class "bg-base-light text-base-light-text h-[calc(100vh_-_52px-_37px)] overflow-auto flex w-full h-full"}
    [:div
     {:class "flex-1 flex justify-center px-4 py-6 mx-auto xl:w-5xl min-h-0"}
     [vaults-config]]]])

(defmethod routing/render-view :settings/zap-vaults
  [_]
  (rf/dispatch [::event/init])
  (if (session/is-admin?)
    [default-page-layout/view
     [page]]
    [not-found/not-found-view]))
